/**
 * Service Worker utility functions for forcing updates and managing the service worker
 */

/**
 * Force an immediate update check for the Service Worker
 * This will check for a new version and activate it if available
 */
export async function forceServiceWorkerUpdate(): Promise<boolean> {
  if (!('serviceWorker' in navigator)) {
    console.warn('Service Workers are not supported in this browser');
    return false;
  }

  try {
    const registration = await navigator.serviceWorker.getRegistration();
    
    if (!registration) {
      console.warn('No Service Worker registration found');
      return false;
    }

    // Force update check
    await registration.update();
    console.log('Service Worker update check triggered');
    
    return true;
  } catch (error) {
    console.error('Failed to force Service Worker update:', error);
    return false;
  }
}

/**
 * Unregister all Service Workers and clear all caches
 * Use this for debugging or to force a complete reset
 */
export async function unregisterServiceWorkers(): Promise<boolean> {
  if (!('serviceWorker' in navigator)) {
    console.warn('Service Workers are not supported in this browser');
    return false;
  }

  try {
    // Unregister all service workers
    const registrations = await navigator.serviceWorker.getRegistrations();
    await Promise.all(registrations.map(registration => registration.unregister()));
    
    // Clear all caches
    const cacheNames = await caches.keys();
    await Promise.all(cacheNames.map(cacheName => caches.delete(cacheName)));
    
    console.log('All Service Workers unregistered and caches cleared');
    return true;
  } catch (error) {
    console.error('Failed to unregister Service Workers:', error);
    return false;
  }
}

/**
 * Get the current Service Worker registration status
 */
export async function getServiceWorkerStatus(): Promise<{
  registered: boolean;
  active: boolean;
  installing: boolean;
  waiting: boolean;
  scope: string | undefined;
}> {
  if (!('serviceWorker' in navigator)) {
    return {
      registered: false,
      active: false,
      installing: false,
      waiting: false,
      scope: undefined
    };
  }

  try {
    const registration = await navigator.serviceWorker.getRegistration();
    
    if (!registration) {
      return {
        registered: false,
        active: false,
        installing: false,
        waiting: false,
        scope: undefined
      };
    }

    return {
      registered: true,
      active: !!registration.active,
      installing: !!registration.installing,
      waiting: !!registration.waiting,
      scope: registration.scope
    };
  } catch (error) {
    console.error('Failed to get Service Worker status:', error);
    return {
      registered: false,
      active: false,
      installing: false,
      waiting: false,
      scope: undefined
    };
  }
}

/**
 * Expose force update function globally for console access
 * Usage: window.forceServiceWorkerUpdate()
 */
if (typeof window !== 'undefined') {
  (window as any).forceServiceWorkerUpdate = forceServiceWorkerUpdate;
  (window as any).unregisterServiceWorkers = unregisterServiceWorkers;
  (window as any).getServiceWorkerStatus = getServiceWorkerStatus;
}

